import { User01Icon } from '@vizit/icons';
import { LibraryEmptyState } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import { Table, TableBody } from 'react-aria-components';
import { UserRow } from '../user-row';
import { tableClasses, tableContainerClasses, usersTableWrapperClasses } from './users-table.classes';
import { UsersTableHeader } from './users-table.header';
import type { UserData } from './users-table.types';

interface UsersTableProps {
  /**
   * ARIA label for the table
   *
   * @optional
   * @type string
   * @default "Users table"
   */
  'aria-label'?: string;
  /**
   * Additional CSS classes to apply to the table
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * Additional CSS classes to apply to the table container
   *
   * @optional
   * @type string
   */
  containerClassName?: string;
  /**
   * Whether the table is in a loading state
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Array of user data to display in the table
   *
   * @required
   * @type UserData[]
   */
  users: UserData[];
  /**
   * Number of skeleton rows to show when loading
   *
   * @optional
   * @type number
   * @default 5
   */
  skeletonRowCount?: number;
}

/**
 * UsersTable displays a complete table of users with header and rows.
 * Shows user email addresses in a read-only format.
 *
 * @param props - The props for the UsersTable component
 * @example
 * <UsersTable
 *   users={[
 *     {
 *       id: "user-123",
 *       email_address: "john.doe@example.com"
 *     },
 *     {
 *       id: "user-456",
 *       email_address: "jane.smith@example.com"
 *     }
 *   ]}
 *   isLoading={false}
 *   skeletonRowCount={5}
 * />
 */

function UsersTable(props: UsersTableProps) {
  const {
    'aria-label': ariaLabel = 'Users table',
    className,
    containerClassName,
    isLoading = false,
    users,
    skeletonRowCount = 5,
    ...other
  } = props;

  if (users.length === 0 && !isLoading) {
    return (
      <LibraryEmptyState
        heading="No users found"
        description="There are no users in this organization yet."
        icon={User01Icon}
      />
    );
  }

  return (
    <div className={usersTableWrapperClasses()}>
      <div className={cx(tableContainerClasses(), containerClassName)}>
        <Table className={tableClasses()} aria-label={ariaLabel} {...other}>
          <UsersTableHeader />
          <TableBody>
            {isLoading
              ? Array.from({ length: skeletonRowCount }, (_, index) => {
                  const skeletonId = `skeleton-row-${index}`;
                  return (
                    <UserRow
                      key={skeletonId}
                      data-index={index}
                      user={{
                        id: skeletonId,
                        email_address: '',
                      }}
                      isLoading={true}
                    />
                  );
                })
              : users.map((user, index) => (
                  <UserRow key={`user-row-${user?.id || index}`} data-index={index} user={user} />
                ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export { UsersTable };
export type { UsersTableProps, UserData };
